/**
 * Who may administer an organization — the one vocabulary, and the one rule.
 *
 * `OrganizationMember.role` and `OrganizationInvitation.role` are a Postgres
 * enum (`organization_role`, packages/database/src/schema/organization.ts), so
 * `admin` and `member` are the only two values those columns can hold. This
 * module is where that closed set is written down, and the Drizzle enum is
 * built from `ORGANIZATION_ROLES` rather than from a second list that could
 * drift from it.
 *
 * It lives in `@repo/shared` because the rule has four readers in three
 * packages — the domain API (`resolveAccess`), eve's organisation-context
 * write (`apps/agents/amoreg/lib/org-context-edit.ts`), the skills toolkit
 * (`apps/server/src/mcp/toolkits/governance/skills.ts`) and the membership
 * writes themselves. Each of those used to spell the rule out for itself, and
 * they had already drifted: the skills toolkit admitted a third role,
 * `owner`, that no other reader honoured, so a person could manage the
 * organisation's skills while the settings page told them they could not.
 * `canManageOrganization` is now the only place that expression exists.
 *
 * Nothing here reads the database or decides *who* the actor is; it answers
 * only what a resolved role and global-admin flag mean. Callers still resolve
 * both against live rows, and still fail closed when they cannot.
 *
 * @module organizationRole
 */

/**
 * Every role an organization membership can carry, in the order the database
 * enum declares them. The Drizzle `pgEnum` is built from this tuple.
 */
export const ORGANIZATION_ROLES = ["admin", "member"] as const;

/** A membership role, as the `organization_role` Postgres enum stores it. */
export type OrganizationRole = (typeof ORGANIZATION_ROLES)[number];

/** The role a new membership or invitation carries when none is stated. */
export const DEFAULT_ORGANIZATION_ROLE: OrganizationRole = "member";

/**
 * Historical values the column held before it was a real enum, and the role
 * each was migrated onto.
 *
 * The column was a bare `varchar` with no length and no check from migration
 * 0008 until the enum landed, so it accepted anything a WorkOS import or a
 * hand-written fix happened to write. `owner` is the one such value the code
 * ever acknowledged, and only in the skills toolkit, which treated it as a
 * manager — so `owner` becomes `admin`, both to keep the access those rows
 * already exercised and because demoting an organisation's only owner would
 * leave it with nobody able to administer it ever again.
 *
 * This map is the migration's rule as well: migration 0065 applies exactly
 * these mappings before the cast. It is kept here so the two cannot disagree.
 */
export const LEGACY_ORGANIZATION_ROLES: Readonly<Record<string, OrganizationRole>> = {
  owner: "admin",
};

/** Whether a value is already one of the two roles the column can hold. */
export const isOrganizationRole = (value: unknown): value is OrganizationRole =>
  typeof value === "string" && (ORGANIZATION_ROLES as readonly string[]).includes(value);

/**
 * A role as one of the two the database now guarantees.
 *
 * Since the enum landed, a value read from `OrganizationMember.role` is
 * already `admin` or `member` — the database will not store anything else.
 * This exists for the edges where a role arrives as an unvalidated string
 * anyway: a roster payload crossing the MCP boundary, a WorkOS profile, a
 * fixture written before the migration. Unknown input becomes `member`, never
 * `admin`: a value nobody recognises is not evidence of authority.
 */
export const normalizeOrganizationRole = (value: unknown): OrganizationRole => {
  if (isOrganizationRole(value)) return value;
  if (typeof value !== "string") return DEFAULT_ORGANIZATION_ROLE;
  const folded = value.trim().toLowerCase();
  if (isOrganizationRole(folded)) return folded;
  return LEGACY_ORGANIZATION_ROLES[folded] ?? DEFAULT_ORGANIZATION_ROLE;
};

/**
 * May this person change the organization — its profile, its regimes, its
 * skills and its people?
 *
 * The organization's own admins, and Amoreg operators (active global admins)
 * acting on their behalf. `role` is the actor's membership role as the
 * database holds it, or null when they hold no membership at all; a global
 * admin with no membership still manages, which is how support acts in an
 * organisation it never joined.
 *
 * Deliberately takes a resolved role rather than a user id: every caller
 * already has one, and reading the row here would hide a database call inside
 * what reads like a pure check.
 */
export const canManageOrganization = (input: {
  readonly role: OrganizationRole | null | undefined;
  readonly globalAdmin: boolean;
}): boolean => input.role === "admin" || input.globalAdmin === true;
